import React, { useEffect } from 'react'
import { Card, CardContent, Typography, Box, Checkbox, FormControlLabel, Button, Radio, RadioGroup, FormControl, FormLabel, TextField } from '@mui/material'
import DescriptionIcon from '@mui/icons-material/Description'
import WorkIcon from '@mui/icons-material/Work'
import BusinessIcon from '@mui/icons-material/Business'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EditIcon from '@mui/icons-material/Edit'

const JobPreferences = ({ 
  jobDescriptionPreferences,
  handleJobPreferencesChange,
  handleJobPreferencesSubmit,
  setConfigStep,
  CONFIG_STEPS,
  hospitalInfo,
  campuses,
  jobDetails,
  fetchHospitalDescriptionFromInternet
}) => {

  // Pre-populate fields based on data from previous screens
  useEffect(() => {
    // If hospital description exists, enable and pre-populate hospital description
    if (hospitalInfo?.description) {
      handleJobPreferencesChange('includeHospitalDescription', 'enabled', true)
      handleJobPreferencesChange('includeHospitalDescription', 'source', 'manual')
      handleJobPreferencesChange('includeHospitalDescription', 'customContent', hospitalInfo.description)
    }

    // If hospital benefits exist, enable and pre-populate hospital benefits
    if (hospitalInfo?.benefits) {
      handleJobPreferencesChange('includeHospitalBenefits', 'enabled', true)
      handleJobPreferencesChange('includeHospitalBenefits', 'source', 'manual')
      handleJobPreferencesChange('includeHospitalBenefits', 'customContent', hospitalInfo.benefits)
    }

    // If hospital culture exists, enable and pre-populate hospital culture
    if (hospitalInfo?.culture) {
      handleJobPreferencesChange('includeHospitalCulture', 'enabled', true)
      handleJobPreferencesChange('includeHospitalCulture', 'source', 'manual')
      handleJobPreferencesChange('includeHospitalCulture', 'customContent', hospitalInfo.culture)
    }

    // If location details exist, enable and pre-populate location details
    if (hospitalInfo?.address || hospitalInfo?.city || hospitalInfo?.state) {
      handleJobPreferencesChange('includeLocationDetails', 'enabled', true)
      handleJobPreferencesChange('includeLocationDetails', 'source', 'manual')
      
      const locationDetails = []
      if (hospitalInfo.address) locationDetails.push(hospitalInfo.address)
      if (hospitalInfo.city && hospitalInfo.state) {
        locationDetails.push(`${hospitalInfo.city}, ${hospitalInfo.state}`)
      } else if (hospitalInfo.city) {
        locationDetails.push(hospitalInfo.city)
      } else if (hospitalInfo.state) {
        locationDetails.push(hospitalInfo.state)
      }
      
      handleJobPreferencesChange('includeLocationDetails', 'customContent', locationDetails.join('\n'))
    }

    // If campuses exist, enable community info
    if (campuses && campuses.length > 0) {
      handleJobPreferencesChange('includeCommunityInfo', 'enabled', true)
      
      // If any campus has a description, use it for community info
      const campusDescriptions = campuses
        .filter(campus => campus.description)
        .map(campus => campus.description)
      
      if (campusDescriptions.length > 0) {
        handleJobPreferencesChange('includeCommunityInfo', 'source', 'manual')
        handleJobPreferencesChange('includeCommunityInfo', 'customContent', campusDescriptions.join('\n\n'))
      }
    }

    // If job details exist, enable job requirements
    if (jobDetails?.details) {
      const detailsEntries = Object.entries(jobDetails.details)
      if (detailsEntries.length > 0) {
        handleJobPreferencesChange('includeJobRequirements', 'enabled', true)
        
        // Format details for job requirements
        const requirements = detailsEntries
          .filter(([, value]) => value.value !== undefined && value.value !== '')
          .map(([key, value]) => {
            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
            return `${label}: ${value.value}`
          })
        
        if (requirements.length > 0) {
          handleJobPreferencesChange('includeJobRequirements', 'source', 'manual')
          handleJobPreferencesChange('includeJobRequirements', 'customContent', requirements.join('\n'))
        }
      }
    }
  }, [hospitalInfo, campuses, jobDetails])

  // Handle source change to fetch AI-generated content when needed
  const handleSourceChange = async (key, value) => {
    handleJobPreferencesChange(key, 'source', value)
    
    // If user selects AI generation and we have a hospital name, fetch the content
    if (value === 'ai' && hospitalInfo?.name) {
      // Show loading state
      handleJobPreferencesChange(key, 'customContent', 'Loading AI-generated content...')
      
      // Fetch the content
      const description = await fetchHospitalDescriptionFromInternet(key, hospitalInfo.name)
      // Update with the fetched content
      if (description) {
        handleJobPreferencesChange(key, 'customContent', description)
      } else {
        handleJobPreferencesChange(key, 'customContent', 'Failed to generate content. Please try again or use manual content.')
      }
    }
  }

  const sections = [
    {
      title: 'Hospital Information',
      icon: <BusinessIcon />,
      items: [
        { key: 'includeHospitalDescription', label: 'Hospital Description' },
        { key: 'includeHospitalBenefits', label: 'Hospital Benefits' },
        { key: 'includeHospitalCulture', label: 'Hospital Culture' }
      ]
    },
    {
      title: 'Location Details',
      icon: <LocationOnIcon />,
      items: [
        { key: 'includeLocationDetails', label: 'Location Information' },
        { key: 'includeCommunityInfo', label: 'Community Information' },
        { key: 'includeCostOfLiving', label: 'Cost of Living' }
      ]
    },
    {
      title: 'Position Details',
      icon: <WorkIcon />,
      items: [
        { key: 'includeJobRequirements', label: 'Job Requirements' },
        { key: 'includeCompensation', label: 'Compensation Details' },
        { key: 'includeSchedule', label: 'Schedule Information' }
      ]
    },
    {
      title: 'Additional Information',
      icon: <DescriptionIcon />,
      items: [
        { key: 'includeGrowthOpportunities', label: 'Growth Opportunities' },
        { key: 'includeResearchOpportunities', label: 'Research Opportunities' },
        { key: 'includeTeachingOpportunities', label: 'Teaching Opportunities' }
      ]
    }
  ]

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Job Description Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Select which information you'd like to include in the job descriptions and choose whether to use AI-generated content or provide your own.
        </Typography>

        <Box sx={{ mt: 3 }}>
          {sections.map((section) => (
            <Box key={section.title} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ mr: 1, color: 'primary.main' }}>
                  {section.icon}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  {section.title}
                </Typography>
              </Box>
              {section.items.map((item) => (
                <Box key={item.key} sx={{ ml: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={jobDescriptionPreferences[item.key].enabled}
                        onChange={(e) => handleJobPreferencesChange(item.key, 'enabled', e.target.checked)}
                      />
                    }
                    label={item.label}
                  />
                  {jobDescriptionPreferences[item.key].enabled && (
                    <Box sx={{ ml: 4, mt: 1 }}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Content Source</FormLabel>
                        <RadioGroup
                          value={jobDescriptionPreferences[item.key].source}
                          onChange={(e) => handleSourceChange(item.key, e.target.value)}
                        >
                          <FormControlLabel value="ai" control={<Radio />} label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AutoAwesomeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="body2">AI-generated based on hospital information</Typography>
                            </Box>
                          } />
                          <FormControlLabel value="manual" control={<Radio />} label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <EditIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="body2">Use our provided content</Typography>
                            </Box>
                          } />
                        </RadioGroup>
                      </FormControl>
                      {jobDescriptionPreferences[item.key].source === 'manual' && (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={jobDescriptionPreferences[item.key].customContent}
                          onChange={(e) => handleJobPreferencesChange(item.key, 'customContent', e.target.value)}
                          placeholder={`Enter custom ${item.label.toLowerCase()}...`}
                          sx={{ mt: 1 }}
                        />
                      )}
                      {jobDescriptionPreferences[item.key].source === 'ai' && (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={jobDescriptionPreferences[item.key].customContent}
                          onChange={(e) => handleJobPreferencesChange(item.key, 'customContent', e.target.value)}
                          placeholder={`AI-generated ${item.label.toLowerCase()}...`}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button 
            variant="outlined" 
            onClick={() => setConfigStep(CONFIG_STEPS.DETAILS)}
          >
            Back
          </Button>
          <Button 
            variant="contained" 
            onClick={handleJobPreferencesSubmit}
          >
            Continue
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default JobPreferences